import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Printer } from 'lucide-react';
import Modal from '../components/Modal';
import api from '../services/api';
import { formatCurrency, today } from '../utils/helpers';

const COLUMNS = [
  { key: 'dc_no', label: 'DC No.', required: true, placeholder: 'e.g. 1024' },
  { key: 'ledger_no', label: 'Ledger No', required: true, placeholder: 'Auto-filled from party' },
  { key: 'party_name', label: 'Sales Party Name', required: true, options: 'parties', placeholder: 'Select sales party' },
  { key: 'kms', label: 'KMs', num: true, placeholder: 'e.g. 45' },
  { key: 'to', label: 'To', placeholder: 'Destination' },
  { key: 'vehicle_no', label: 'Vehicle No', upper: true, placeholder: 'e.g. TS09AB1234' },
  { key: 'material', label: 'Material', required: true, options: 'materials', placeholder: 'Select material' },
  { key: 'qty', label: 'Qty', required: true, num: true, placeholder: 'e.g. 10' },
  { key: 'material_amount', label: 'Material Amount', num: true, money: true, placeholder: '0.00' },
  { key: 'hire_amount', label: 'Hire Amount', num: true, money: true, placeholder: '0.00' },
  { key: 'total_amount', label: 'Total Amount', num: true, money: true, computed: true },
  { key: 'crusher_name', label: 'Purchase Crusher Name', placeholder: 'Crusher name' },
  { key: 'pdc_no', label: 'PDC No', placeholder: 'e.g. 556677' },
];

const emptyForm = () => ({
  dc_no: '', ledger_no: '', party_name: '', kms: '', to: '', vehicle_no: '',
  material: '', qty: '', material_amount: '', hire_amount: '', crusher_name: '', pdc_no: '',
});

const ENDPOINT = '/daily_sheet.php';

const num = (v) => parseFloat(v) || 0;
const errMsg = (err, fallback) => err?.response?.data?.message || fallback;
// DB returns "0.00" for empty numeric fields; show those as blank in the form
const toForm = (e) => {
  const f = emptyForm();
  Object.keys(f).forEach(k => {
    const v = e[k] ?? '';
    const col = COLUMNS.find(c => c.key === k);
    f[k] = col?.num && num(v) === 0 ? '' : String(v);
  });
  return f;
};
// Only approved, active master records are offered in the dropdowns
const usable = (rows) => (rows || []).filter(r => r.approval_status === 'approved' && Number(r.status) !== 0);
// Returns { field: message } for every required field that is missing/invalid
const validate = (f) => {
  const errors = {};
  COLUMNS.filter(c => c.required).forEach(c => {
    const v = String(f[c.key] ?? '').trim();
    if (v === '') errors[c.key] = `${c.label.replace(/\.$/, '')} is required`;
    else if (c.num && num(v) <= 0) errors[c.key] = `${c.label} must be greater than 0`;
  });
  return errors;
};
const rowTotal = (r) => num(r.material_amount) + num(r.hire_amount);

export default function DailySheet() {
  const [date, setDate] = useState(today());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [parties, setParties] = useState([]);
  const [materials, setMaterials] = useState([]);

  useEffect(() => {
    api.get('/ledgers', { params: { type: 'customer' } })
      .then(r => setParties(usable(r.data.data)))
      .catch(() => toast.error('Failed to load parties'));
    api.get('/products')
      .then(r => setMaterials(usable(r.data.data)))
      .catch(() => toast.error('Failed to load materials'));
  }, []);

  const optionNames = (key) => {
    const names = [...new Set((key === 'parties' ? parties : materials).map(x => x.name))]
      .sort((a, b) => a.localeCompare(b));
    return names;
  };

  // Picking a party also fills Ledger No from the ledger's code
  const onSelect = (c, value) => {
    const next = { ...form, [c.key]: value };
    if (c.key === 'party_name') {
      const ledger = parties.find(l => l.name === value);
      if (ledger?.ledger_code) next.ledger_no = ledger.ledger_code;
    }
    setForm(next);
    clearError(c.key, next);
  };

  // Drop a field's error once the user fixes it (Ledger No may be filled by party select)
  const clearError = (key, next) => {
    if (!Object.keys(errors).length) return;
    const now = validate(next);
    setErrors(es => {
      const copy = { ...es };
      [key, 'ledger_no'].forEach(k => { if (!now[k]) delete copy[k]; });
      return copy;
    });
  };

  const load = useCallback(() => {
    if (!date) return;
    setLoading(true);
    api.get(ENDPOINT, { params: { date } })
      .then(r => setEntries(r.data.data || []))
      .catch(err => toast.error(errMsg(err, 'Failed to load daily sheet')))
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setForm(emptyForm()); setErrors({}); setOpen(true); };
  const openEdit = (e) => { setEditId(e.id); setForm(toForm(e)); setErrors({}); setOpen(true); };

  const save = async () => {
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error('Please fill all required fields');
    setSaving(true);
    try {
      const body = { ...form, sheet_date: date };
      const r = editId
        ? await api.put(ENDPOINT, body, { params: { id: editId } })
        : await api.post(ENDPOINT, body);
      toast.success(r.data.message || (editId ? 'Entry updated' : 'Entry added'));
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to save entry'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.delete(ENDPOINT, { params: { id } });
      toast.success('Entry deleted');
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to delete entry'));
    }
  };

  const totals = entries.reduce((t, r) => ({
    kms: t.kms + num(r.kms),
    qty: t.qty + num(r.qty),
    material_amount: t.material_amount + num(r.material_amount),
    hire_amount: t.hire_amount + num(r.hire_amount),
    total_amount: t.total_amount + rowTotal(r),
  }), { kms: 0, qty: 0, material_amount: 0, hire_amount: 0, total_amount: 0 });

  const cell = (r, c) => {
    const v = c.computed ? rowTotal(r) : r[c.key];
    if (c.money) return formatCurrency(v);
    if (c.num) return num(v) === 0 ? '' : num(v);
    return v;
  };

  return (
    <div className="card ds-card">
      <div className="ds-head">
        <div className="ds-date">
          <label htmlFor="ds-date">Date</label>
          <input id="ds-date" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <h3 className="ds-title">DAILY SHEET</h3>
        <div className="ds-actions no-print">
          <button className="btn btn-gray" onClick={() => window.print()}><Printer size={14} /> Print</button>
          <button className="btn btn-blue" onClick={openAdd}><Plus size={14} /> Add Entry</button>
        </div>
      </div>

      <div className="table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th className="ds-sno">#</th>
              {COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}
              <th className="no-print">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLUMNS.length + 2} className="empty">Loading...</td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 2} className="empty">
                  No entries for this day yet. Click <strong>Add Entry</strong> to start.
                </td>
              </tr>
            ) : entries.map((r, i) => (
              <tr key={r.id}>
                <td className="ds-sno">{i + 1}</td>
                {COLUMNS.map(c => (
                  <td key={c.key} className={(c.num ? 'ds-num' : '') + (c.computed ? ' ds-computed' : '')}>{cell(r, c)}</td>
                ))}
                <td className="no-print">
                  <div className="ds-row-actions">
                    <button className="btn btn-gray btn-sm" onClick={() => openEdit(r)}><Pencil size={12} /> Edit</button>
                    <button className="btn btn-red btn-sm" onClick={() => remove(r.id)} aria-label="Delete entry"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="ds-grand">
              <td></td>
              <td colSpan={3} className="ds-grand-label">Grand Total</td>
              <td className="ds-num">{totals.kms ? +totals.kms.toFixed(2) : ''}</td>
              <td colSpan={3}></td>
              <td className="ds-num">{totals.qty ? +totals.qty.toFixed(3) : ''}</td>
              <td className="ds-num">{formatCurrency(totals.material_amount)}</td>
              <td className="ds-num">{formatCurrency(totals.hire_amount)}</td>
              <td className="ds-num">{formatCurrency(totals.total_amount)}</td>
              <td colSpan={2}></td>
              <td className="no-print"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? 'Edit Entry' : 'Add Entry'} size="lg"
        footer={<>
          <button className="btn btn-gray" onClick={() => setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save} disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Add'}</button>
        </>}>
        <div className="form-grid">
          {COLUMNS.map(c => (
            <div className="form-group" key={c.key}>
              <label>{c.label}{c.required ? ' *' : ''}</label>
              {c.computed ? (
                <input value={rowTotal(form).toFixed(2)} readOnly className="ds-readonly" />
              ) : c.options ? (
                <select value={form[c.key]} onChange={e => onSelect(c, e.target.value)} className={errors[c.key] ? 'ds-invalid' : ''}>
                  <option value="">{c.placeholder}</option>
                  {/* keep a saved value visible even if it's no longer in the master list */}
                  {form[c.key] && !optionNames(c.options).includes(form[c.key]) && (
                    <option value={form[c.key]}>{form[c.key]}</option>
                  )}
                  {optionNames(c.options).map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              ) : (
                <input
                  type={c.num ? 'number' : 'text'}
                  value={form[c.key]}
                  placeholder={c.placeholder}
                  className={errors[c.key] ? 'ds-invalid' : ''}
                  onChange={e => {
                    const next = { ...form, [c.key]: c.upper ? e.target.value.toUpperCase() : e.target.value };
                    setForm(next);
                    clearError(c.key, next);
                  }}
                />
              )}
              {errors[c.key] && <div className="ds-error">{errors[c.key]}</div>}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
